import { Button, TextInput } from '@mantine/core';
import { isEmail, useForm } from '@mantine/form';
import { FC, useState } from 'react';

import { Page } from '@/components/Page.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { createMagicLink, googleSignIn } from '@/services/authService.ts';

import { useUIStore } from '@/stores/uiStore.ts';

const LoginPage: FC = () => {
    const is_fancy_checkin_rendered = useUIStore((state) => state.is_fancy_checkin_rendered);
    const [external_sign_in, setExternalSignIn] = useState(false);
    const [is_magic_link_created, setIsMagicLinkCreated] = useState(false);
    const [has_tried, setHasTried] = useState(false);
    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            email: '',
        },
        validateInputOnBlur: true,

        validate: {
            email: isEmail('Ungültige E-Mail Adresse'),
        },
    });

    const magicLinkSignIn = async (): Promise<void> => {
        form.validate();
        if (!form.isValid('email')) return;

        const is_created = await createMagicLink(form.getValues().email);
        setIsMagicLinkCreated(is_created);
        setHasTried(true);
    };

    if (is_fancy_checkin_rendered) {
        return (
            <Page className="relative flex-row bg-black" fullScreen>
                <div className="relative flex w-full flex-col items-center justify-center">
                    <div className="flex w-[40rem] flex-col items-center justify-center gap-5 rounded-md border border-white/10 bg-white/5 py-20 shadow-lg shadow-white/5 backdrop-blur-sm">
                        <h1 className="text-4xl text-white">CareFlow Login</h1>
                        <div className="flex min-h-[10rem] w-1/2 flex-col justify-center gap-5 text-center">
                            <TextInput
                                classNames={{
                                    input: 'border-white !bg-black text-white',
                                }}
                                className="text-start"
                                placeholder="E-Mail Adresse"
                                {...form.getInputProps('email')}
                            />
                            <Button disabled={!!form.errors.email} onClick={magicLinkSignIn}>
                                Schicke mir den Login-Link per Email
                            </Button>
                            {has_tried && is_magic_link_created && (
                                <Paragraph className="text-white">{`Falls ein Account mit dieser Email-Adresse existiert, wurde Dir ein Anmeldelink an ${form.values.email} zugeschickt`}</Paragraph>
                            )}
                            {has_tried && !is_magic_link_created && (
                                <Paragraph className="text-white">{`Erstellung des Anmeldelinks für ${form.values.email} fehlgeschlagen`}</Paragraph>
                            )}
                            <div className="flex items-center gap-2 text-sm text-white">
                                <hr className="w-full border-t border-white/20" />
                                <span>oder</span>
                                <hr className="w-full border-t border-white/20" />
                            </div>
                            <Button
                                onClick={googleSignIn}
                                variant="outline"
                                color="white"
                                className="text-white hover:bg-white/10 hover:text-white"
                                leftSection={
                                    <img
                                        src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg"
                                        alt="Google Logo"
                                    />
                                }
                            >
                                Google SSO
                            </Button>
                        </div>
                    </div>
                </div>
            </Page>
        );
    }

    return (
        <Page className="flex-row" fullScreen>
            <img
                className="h-screen w-[60%] object-cover"
                src="https://assets.website-files.com/61fd7628494324361585dec9/6429a8e8f5406635e276e77d_20230330-MAP_2328-p-2000.jpg"
                alt="Eterno Wartebereich"
            />
            <div className="flex flex-1 flex-col items-center justify-center gap-5">
                <h1 className="text-4xl">CareFlow Login</h1>
                <div className="flex min-h-[10rem] w-2/3 flex-col justify-center gap-5 text-center">
                    {!external_sign_in && (
                        <>
                            <Button onClick={googleSignIn}>Eterno Mitarbeiter Login mit Google</Button>
                            <Button onClick={() => setExternalSignIn(true)} variant="outline">
                                Externe Anmeldung
                            </Button>
                        </>
                    )}
                    {external_sign_in && !is_magic_link_created && (
                        <>
                            <TextInput
                                className="text-start"
                                placeholder="E-Mail Adresse"
                                {...form.getInputProps('email')}
                            />
                            <Button disabled={!!form.errors.email} onClick={magicLinkSignIn}>
                                Schicke mir den Login-Link per Email
                            </Button>
                        </>
                    )}
                    {has_tried && is_magic_link_created && (
                        <Paragraph>{`Falls ein Account mit dieser Email-Adresse existiert, wurde Dir ein Anmeldelink an ${form.values.email} zugeschickt`}</Paragraph>
                    )}
                    {has_tried && !is_magic_link_created && (
                        <Paragraph>{`Erstellung des Anmeldelinks für ${form.values.email} fehlgeschlagen`}</Paragraph>
                    )}
                </div>
            </div>
        </Page>
    );
};

export default LoginPage;
